import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
  FormsModule,
  ReactiveFormsModule,
  UntypedFormArray,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatRadioModule } from '@angular/material/radio';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';
import { And, BoolEquals, Or, Rule, RuleInterface, StringEquals } from '@vendasta/automata';
import { GalaxyAlertModule } from '@vendasta/galaxy/alert';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { GalaxyWrapModule } from '@vendasta/galaxy/galaxy-wrap';
import { GalaxyInputModule, SelectInputOptionInterface } from '@vendasta/galaxy/input';
import { GalaxyPipesModule } from '@vendasta/galaxy/pipes';
import { BehaviorSubject, Observable, Subject, combineLatest, of } from 'rxjs';
import { map, shareReplay, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { AutomationViewService } from '../../../../../automation/automation-view.service';
import { AppAndNames, AppsService } from '../../../../../data-services/apps.service';
import { CommonTriggerPanelComponent } from '../../../common/common-trigger-panel.component';
import { TriggerBaseRules } from '../../../common/decorators/trigger-rules.decorator';

const Succeeds = 'succeeds';
const Fails = 'fails';
const SelectProduct = 'select-product';
const AnyProduct = 'any-product';

@Component({
  selector: 'automata-wholesale-purchase-created-panel',
  templateUrl: './wholesale-purchase-created-panel.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./wholesale-purchase-created-panel.component.scss'],
  imports: [
    CommonModule,
    TranslateModule,
    GalaxyAlertModule,
    MatButtonModule,
    MatCardModule,
    FormsModule,
    GalaxyFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
    MatIconModule,
    MatSelectModule,
    GalaxyPipesModule,
    GalaxyInputModule,
    MatRadioModule,
    GalaxyWrapModule,
  ],
})
export class WholesalePurchaseCreatedPanelComponent extends CommonTriggerPanelComponent implements OnInit, OnDestroy {
  @Input() compareRejectedReason: boolean;
  @Input() descriptionTranslationId: string;
  @Input() includeAnyProducts = true;
  SELECT_PRODUCT = SelectProduct;
  ANY_PRODUCT = AnyProduct;

  apps$: Observable<AppAndNames[]>;
  allProductsControl: UntypedFormControl = new UntypedFormControl('', Validators.required);
  productControl: UntypedFormControl = new UntypedFormControl('', Validators.required);
  appOptions$: Observable<SelectInputOptionInterface[]>;

  statusControl: UntypedFormControl = new UntypedFormControl('', Validators.required);

  options: SelectInputOptionInterface[] = [
    { label: 'AUTOMATIONS.EDITOR.TRIGGERS.WHOLESALE_PURCHASE_CREATED.SUCCEEDS', value: Succeeds },
    { label: 'AUTOMATIONS.EDITOR.TRIGGERS.WHOLESALE_PURCHASE_CREATED.FAILS', value: Fails },
  ];

  destroyed$$ = new Subject<void>();

  templateView = this.automationViewService.isTemplateView();

  disableProductSelection$$ = new BehaviorSubject(false);
  disableProductSelection$ = this.disableProductSelection$$.asObservable();

  constructor(
    private readonly automationViewService: AutomationViewService,
    private readonly appsService: AppsService,
  ) {
    super();
  }

  ngOnInit(): void {
    super.ngOnInit();
    if (this.triggerChoices?.rules?.length > 0) {
      const and = And.fromRuleInterface(this.triggerChoices?.rules[0]);
      const approved = and.listValuesByVariablePath('{.approved}');
      if (!!approved && approved.length > 0) {
        const statusFromRule = approved[0] ? 'succeeds' : 'fails';
        this.statusControl.setValue(statusFromRule);
      }

      const andRules = And.fromRuleInterface(this.triggerChoices.rules[0]).value as Rule[];
      const products = [];

      if (andRules.length > 1) {
        const orRules = andRules[1].value as Rule[];
        this.disableProductSelection$$.next(false);
        this.allProductsControl.setValue(SelectProduct);
        orRules.map((lineItem) => {
          products.push(this.appsService.appKeyToKeySku({ appId: lineItem.value as string }));
        });
      } else {
        this.disableProductSelection$$.next(true);
        this.allProductsControl.setValue(AnyProduct);
        this.savingEnabled$.next(true);
      }
      this.productControl.setValue(products);
    } else {
      this.disableProductSelection$$.next(true);
      this.allProductsControl.setValue(AnyProduct);
      this.savingEnabled$.next(true);
    }

    combineLatest([this.templateView, this.allProductsControl.valueChanges])
      .pipe(takeUntil(this.destroyed$$))
      .subscribe(([template, dProducts]) => {
        if (dProducts.valueOf() === AnyProduct || template) {
          this.productControl.disable();
          this.disableProductSelection$$.next(true);
        } else {
          this.productControl.enable();
          this.disableProductSelection$$.next(false);
        }
      });

    this.apps$ = this.disableProductSelection$.pipe(
      switchMap((disableProductSelection) => {
        if (disableProductSelection) {
          return of([]);
        }
        return this.appsService.getProductNames(false, false, false);
      }),
      take(1),
      shareReplay(1),
    );

    this.appOptions$ = this.apps$.pipe(
      map((apps) => {
        return apps.map((app) => {
          const appName = app.name;
          return {
            label: `${appName}`,
            value: this.appsService.appKeyToKeySku({ appId: app.appId, editionId: app.editionId }),
          } as SelectInputOptionInterface;
        });
      }),
      // Once the product and option lists are loaded we can let the user save the panel.
      tap(() => this.savingEnabled$.next(true)),
      take(1),
    );
  }

  ngOnDestroy(): void {
    this.destroyed$$.next();
    this.destroyed$$.unsubscribe();
  }

  // Return true when your form is valid.
  isFormValid(): boolean {
    return !this.productControl.invalid && !this.allProductsControl.invalid && !this.statusControl.invalid;
  }

  getControlsToValidateOnSave(): (UntypedFormControl | UntypedFormGroup | UntypedFormArray)[] {
    return [this.productControl, this.allProductsControl, this.statusControl];
  }

  @TriggerBaseRules
  getBaseRules(): RuleInterface[] {
    return this.getRules();
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  fillRuleValues(rules: RuleInterface[]): RuleInterface[] {
    return this.getRules();
  }

  private getRules(): RuleInterface[] {
    const approved = this.statusControl.value === Succeeds;

    if (this.allProductsControl.value === SelectProduct && this.productControl.value?.length > 0) {
      const products: Array<Rule> = new Array<Rule>();
      this.productControl.value.map((appKeyString) => {
        products.push(new StringEquals('{.sku}', appKeyString));
      });
      const orRule = new Or(products);
      const andRule = new And([new BoolEquals('{.approved}', approved), orRule]);
      return [andRule.toRuleInterface()];
    } else {
      const andRule = new And([new BoolEquals('{.approved}', approved)]);
      return [andRule.toRuleInterface()];
    }
  }

  getProductNameFromString(appKeyString: string): Observable<string> {
    return this.appOptions$.pipe(
      map((appOption) => {
        const match = appOption.find((a) => a.value === appKeyString);
        return match?.label || appKeyString;
      }),
    );
  }
}
